import { LitElement, css, html, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";

import { ApiError, api, downloads, type HistoryItem, type HistoryParams, type SignalState, type User } from "../api/client";
import "../components/ld-signal-strip";
import { formatBirth, formatDateTime, initials } from "../format";
import { icons } from "../icons";
import { navigate } from "../router";
import { DECISIONS, STATES, badgeClass } from "../signal-state";
import { shared } from "../styles/shared";

@customElement("page-history")
export class PageHistory extends LitElement {
  @property({ attribute: false }) user!: User;
  @state() private items: HistoryItem[] = [];
  @state() private loading = true;
  @state() private error = "";
  @state() private filters: HistoryParams = { q: "", date: "", state: "", scope: "mine" };
  @state() private editing: string | null = null;
  @state() private draft = "";
  @state() private rowError = "";
  @state() private busyRow: string | null = null;
  private debounce?: number;

  static styles = [
    shared,
    css`
      :host { display: block; padding-bottom: 80px; }
      .toolbar { display: flex; align-items: center; justify-content: space-between; gap: 16px; flex-wrap: wrap; margin: 28px 0 18px; }
      .filters { display: flex; gap: 10px; flex: 1; min-width: 260px; flex-wrap: wrap; }
      .search { position: relative; flex: 1; min-width: 200px; max-width: 320px; }
      .search svg { position: absolute; left: 14px; top: 50%; transform: translateY(-50%); width: 17px; height: 17px; color: var(--slate-400); }
      .search .input { padding-left: 40px; }
      .filters .input, .filters .select { width: auto; }
      .tools { display: flex; gap: 10px; }
      .table-wrap { border: 1px solid var(--slate-200); border-radius: var(--r-lg); overflow-x: auto; box-shadow: var(--shadow-xs); }
      table { width: 100%; border-collapse: collapse; min-width: 860px; }
      th { text-align: left; font-size: 11px; text-transform: uppercase; letter-spacing: .05em; color: var(--slate-400); font-weight: 700; background: var(--slate-100); padding: 13px 20px; border-bottom: 1px solid var(--slate-200); }
      td { padding: 16px 20px; font-size: 14px; vertical-align: middle; border-bottom: 1px solid var(--slate-200); }
      tr.row:hover td { background: var(--blue-50); }
      .person { display: flex; align-items: center; gap: 12px; }
      .avatar { width: 34px; height: 34px; border-radius: 50%; background: var(--navy-900); color: var(--white); display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 600; flex-shrink: 0; }
      .name { font-weight: 600; font-size: 13.5px; }
      .name:hover { color: var(--blue-600); }
      .meta { font-size: 11.5px; color: var(--slate-400); }
      .date { font-family: var(--font-mono); font-size: 12.5px; color: var(--slate-600); white-space: nowrap; }
      .comment { font-size: 12.5px; color: var(--slate-400); font-style: italic; max-width: 220px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
      .actions { display: flex; gap: 6px; justify-content: flex-end; }
      .comment-box { margin: 0 20px 18px; padding: 14px; background: var(--slate-100); border-radius: var(--r-sm); }
      .comment-box .row-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 8px; }
      .comment-box .alert { margin-top: 10px; }
      .empty { text-align: center; padding: 60px 20px; color: var(--slate-400); }
      .empty svg { width: 40px; height: 40px; margin: 0 auto 14px; color: var(--slate-200); }
      .add { display: flex; align-items: center; justify-content: center; gap: 8px; width: 100%; padding: 16px; border: 1.5px dashed var(--slate-200); border-radius: var(--r-md); color: var(--slate-600); font-weight: 600; font-size: 13.5px; margin-top: 14px; transition: all .18s var(--ease); }
      .add:hover { border-color: var(--blue-500); color: var(--blue-600); background: var(--blue-50); }
      .add svg { width: 16px; height: 16px; }
      .loading { display: flex; justify-content: center; padding: 60px; color: var(--slate-400); }
    `,
  ];

  connectedCallback(): void {
    super.connectedCallback();
    void this.load();
  }

  private async load() {
    this.loading = true;
    this.error = "";
    try {
      this.items = await api.history(this.filters);
    } catch (err) {
      this.error = err instanceof ApiError ? err.message : "Historique indisponible.";
    } finally {
      this.loading = false;
    }
  }

  private setFilter(key: keyof HistoryParams, value: string, debounced = false) {
    this.filters = { ...this.filters, [key]: value };
    window.clearTimeout(this.debounce);
    if (debounced) this.debounce = window.setTimeout(() => void this.load(), 250);
    else void this.load();
  }

  private openComment(item: HistoryItem) {
    this.editing = this.editing === item.id ? null : item.id;
    this.draft = item.review.comment;
    this.rowError = "";
  }

  private async saveComment(item: HistoryItem) {
    this.busyRow = item.id;
    this.rowError = "";
    try {
      await api.review(item.id, { decision: item.review.decision, comment: this.draft, version: item.version });
      this.editing = null;
      await this.load();
    } catch (err) {
      this.rowError = err instanceof ApiError ? err.message : "Enregistrement impossible.";
    } finally {
      this.busyRow = null;
    }
  }

  private async rerun(item: HistoryItem) {
    this.busyRow = item.id;
    try {
      const fresh = await api.rerun(item.id);
      navigate(`/rapports/${fresh.id}`);
    } catch (err) {
      this.error = err instanceof ApiError ? err.message : "Relance impossible.";
    } finally {
      this.busyRow = null;
    }
  }

  private renderRow(item: HistoryItem) {
    const decision = item.review.decision ? DECISIONS[item.review.decision] : null;
    const report = `/rapports/${item.id}`;
    return html`
      <tr class="row">
        <td>
          <div class="person">
            <span class="avatar">${initials(item.person.full_name)}</span>
            <span>
              <a class="name" href=${report}>${item.person.full_name}</a>
              <div class="meta">${item.person.birth_date ? `Naissance : ${formatBirth(item.person.birth_date)}` : "Naissance non renseignée"}${this.filters.scope === "all" ? ` · ${item.created_by_name}` : ""}</div>
            </span>
          </div>
        </td>
        <td><ld-signal-strip compact .items=${item.states}></ld-signal-strip></td>
        <td>${decision ? html`<span class=${badgeClass(decision.tone)}>${decision.label}</span>` : html`<span class="badge badge-neutral">Non revu</span>`}</td>
        <td class="date">${formatDateTime(item.created_at)}</td>
        <td class="comment" title=${item.review.comment || "Aucun commentaire"}>${item.review.comment || "—"}</td>
        <td>
          <div class="actions">
            <a class="icon-btn" href=${report} title="Ouvrir le rapport" aria-label="Ouvrir le rapport">${icons.open}</a>
            <a class="icon-btn" href=${downloads.screeningCsv(item.id)} download title="Télécharger CSV" aria-label="Télécharger CSV">${icons.download}</a>
            <button class="icon-btn" title="Relancer" aria-label="Relancer" ?disabled=${this.busyRow === item.id} @click=${() => this.rerun(item)}>${icons.refresh}</button>
            <button class="icon-btn" title="Commentaire" aria-label="Commentaire" aria-expanded=${this.editing === item.id} @click=${() => this.openComment(item)}>${icons.comment}</button>
          </div>
        </td>
      </tr>
      ${this.editing === item.id
        ? html`<tr><td colspan="6" style="padding:0">
            <div class="comment-box">
              <label class="label" for="c-${item.id}">Commentaire interne</label>
              <textarea class="input" id="c-${item.id}" maxlength="2000" .value=${this.draft}
                @input=${(e: Event) => (this.draft = (e.target as HTMLTextAreaElement).value)}></textarea>
              ${this.rowError ? html`<div class="alert alert-error" role="alert">${icons.alert}<span>${this.rowError}</span></div>` : nothing}
              <div class="row-actions">
                <button class="btn btn-ghost btn-sm" @click=${() => (this.editing = null)}>Annuler</button>
                <button class="btn btn-primary btn-sm" ?disabled=${this.busyRow === item.id} @click=${() => this.saveComment(item)}>Enregistrer</button>
              </div>
            </div>
          </td></tr>`
        : nothing}
    `;
  }

  render() {
    const f = this.filters;
    return html`
      <div class="page-header">
        <div class="container">
          <div class="page-eyebrow">Historique</div>
          <h1 class="page-title">Historique des recherches</h1>
          <p class="page-sub">Chaque criblage est conservé tel qu'il a été obtenu. Une relance crée un nouveau rapport lié au précédent.</p>
        </div>
      </div>
      <div class="container">
        <div class="toolbar">
          <div class="filters">
            <div class="search">
              ${icons.search}
              <input class="input" type="search" placeholder="Rechercher un nom…" aria-label="Rechercher un nom"
                .value=${f.q ?? ""} @input=${(e: Event) => this.setFilter("q", (e.target as HTMLInputElement).value, true)} />
            </div>
            <input class="input" type="date" aria-label="Date" .value=${f.date ?? ""}
              @change=${(e: Event) => this.setFilter("date", (e.target as HTMLInputElement).value)} />
            <select class="select" aria-label="État global" @change=${(e: Event) => this.setFilter("state", (e.target as HTMLSelectElement).value)}>
              <option value="">Tous les états</option>
              ${(Object.keys(STATES) as SignalState[]).map((s) => html`<option value=${s} ?selected=${f.state === s}>${STATES[s].label}</option>`)}
            </select>
            ${this.user?.sees_whole_team
              ? html`<select class="select" aria-label="Périmètre" @change=${(e: Event) => this.setFilter("scope", (e.target as HTMLSelectElement).value)}>
                  <option value="mine" ?selected=${f.scope === "mine"}>Mes recherches</option>
                  <option value="all" ?selected=${f.scope === "all"}>Toute l'équipe</option>
                </select>`
              : nothing}
          </div>
          <div class="tools">
            <a class="btn btn-secondary btn-sm" href=${downloads.historyCsv(f)} download>${icons.download} Télécharger CSV</a>
          </div>
        </div>

        ${this.error ? html`<div class="alert alert-error" role="alert">${icons.alert}<span>${this.error}</span></div>` : nothing}

        <div class="table-wrap">
          <table>
            <thead><tr><th>Nom</th><th>Voyants</th><th>Conclusion</th><th>Date</th><th>Commentaire</th><th style="text-align:right">Actions</th></tr></thead>
            <tbody>
              ${this.loading && !this.items.length
                ? html`<tr><td colspan="6"><div class="loading"><span class="spinner"></span></div></td></tr>`
                : this.items.length
                  ? this.items.map((item) => this.renderRow(item))
                  : html`<tr><td colspan="6"><div class="empty">${icons.empty}<p>Aucune recherche ne correspond à ces critères.</p></div></td></tr>`}
            </tbody>
          </table>
        </div>
        <a class="add" href="/">${icons.plus} Nouvelle recherche</a>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "page-history": PageHistory;
  }
}
